const GOOGLE_CONTACTS_CACHE_KEY = "paboi_google_contacts_token";
const GOOGLE_CONTACTS_TOKEN_TTL_MS = 55 * 60 * 1000;
const PEOPLE_API_CREATE_CONTACT_URL = "https://people.googleapis.com/v1/people:createContact?personFields=names,phoneNumbers,emailAddresses";
const GOOGLE_USERINFO_URL = "https://www.googleapis.com/oauth2/v2/userinfo";
const GOOGLE_CONTACTS_SCOPE = "https://www.googleapis.com/auth/contacts";
const GOOGLE_USERINFO_SCOPES = [
  "https://www.googleapis.com/auth/userinfo.email",
  "https://www.googleapis.com/auth/userinfo.profile",
].join(" ");

export function getGoogleContactsClientId() {
  return process.env.REACT_APP_GOOGLE_CLIENT_ID || null;
}

export function getCachedGoogleContactsToken() {
  try {
    const raw = sessionStorage.getItem(GOOGLE_CONTACTS_CACHE_KEY);
    if (!raw) return null;
    const { access_token, expires_at } = JSON.parse(raw);
    const token = typeof access_token === "string" ? access_token.trim() : "";
    if (!token || !expires_at || Date.now() >= expires_at) return null;
    return token;
  } catch {
    return null;
  }
}

export function setCachedGoogleContactsToken(accessToken) {
  try {
    const token = typeof accessToken === "string" ? accessToken.trim() : "";
    if (!token) return;
    const expires_at = Date.now() + GOOGLE_CONTACTS_TOKEN_TTL_MS;
    sessionStorage.setItem(
      GOOGLE_CONTACTS_CACHE_KEY,
      JSON.stringify({ access_token: token, expires_at })
    );
  } catch (_) {}
}

export function clearGoogleContactsToken() {
  try {
    sessionStorage.removeItem(GOOGLE_CONTACTS_CACHE_KEY);
  } catch (_) {}
}

/**
 * @param {{ name: string, email?: string, member_profile?: { primary_phone?: string, paboi_id?: string } }} memberData
 * @returns {object} Person body for People API createContact
 */
export function buildGoogleContactPersonBody(memberData) {
  const { name, email, member_profile = {} } = memberData;
  const phone = member_profile?.primary_phone || "";
  return {
    names: [{ givenName: name }],
    organizations: [
      {
        name: `PABOI ${("- " + member_profile?.paboi_id) || ""}`,
        title: "Member",
      },
    ],
    ...(phone && {
      phoneNumbers: [{ value: String(phone), type: "mobile" }],
    }),
    ...(email && {
      emailAddresses: [{ value: email }],
    }),
  };
}

/**
 * Mengambil info akun Google yang ter-authorize (email, nama).
 * @param {string} accessToken
 * @returns {Promise<{ email?: string, name?: string }>}
 */
export function fetchGoogleAuthorizedUserInfo(accessToken) {
  return fetch(GOOGLE_USERINFO_URL, {
    headers: { Authorization: `Bearer ${accessToken}` },
  }).then(async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) return {};
    return { email: data.email, name: data.name };
  });
}

/**
 * @param {string} accessToken
 * @param {object} personBody
 * @returns {Promise<object>} Resolves with API response; rejects with { message?, status?: number }
 */
export function createGoogleContactWithToken(accessToken, personBody) {
  return fetch(PEOPLE_API_CREATE_CONTACT_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(personBody),
  }).then(async (res) => {
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = data?.error || { message: res.statusText };
      if (res.status === 401) err.status = 401;
      return Promise.reject(err);
    }
    return data;
  });
}

/**
 * Validasi data member untuk Google Contact.
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateMemberDataForGoogleContact(memberData) {
  if (!memberData) return { valid: false, error: "Data member tidak tersedia" };
  const phone = memberData.member_profile?.primary_phone || "";
  const email = memberData.email || "";
  if (!phone && !email) {
    return { valid: false, error: "Nomor telepon atau email kosong." };
  }
  return { valid: true };
}

/**
 * Menambahkan kontak ke Google Contacts (pakai cache token atau OAuth).
 * @param {{ name: string, email?: string, member_profile?: object }} memberData - Data member (satu item dari detailMember_data)
 * @param {{ onSuccess: (authorizedAccount: { email?: string, name?: string }) => void, onError: (message: string, isTokenExpired?: boolean) => void }} callbacks
 */
export function addToGoogleContact(memberData, { onSuccess, onError }) {
  const clientId = getGoogleContactsClientId();
  if (!clientId) {
    onError("REACT_APP_GOOGLE_CLIENT_ID belum dikonfigurasi di environment.");
    return;
  }

  const validation = validateMemberDataForGoogleContact(memberData);
  if (!validation.valid) {
    onError(validation.error);
    return;
  }

  const personBody = buildGoogleContactPersonBody(memberData);

  const runWithToken = (accessToken) => {
    const token = typeof accessToken === "string" ? accessToken.trim() : "";
    if (!token) {
      clearGoogleContactsToken();
      onError("Sesi Google tidak valid. Silakan coba lagi.", true);
      return;
    }
    createGoogleContactWithToken(token, personBody)
      .then(() => fetchGoogleAuthorizedUserInfo(token).catch(() => ({})))
      .then((authorizedAccount) => {
        onSuccess(authorizedAccount || {});
      })
      .catch((err) => {
        const msg = err?.message || (err?.error && String(err.error)) || String(err);
        const status = err?.status || err?.code;
        const isUnauthenticated =
          status === 401 ||
          (msg && (msg.toLowerCase().includes("unauthenticated") || msg.toLowerCase().includes("missing required authentication")));
        if (isUnauthenticated) {
          clearGoogleContactsToken();
          onError(
            "Sesi Google kedaluwarsa atau tidak valid. Silakan klik tombol lagi untuk login ulang ke Google.",
            true
          );
          return;
        }
        onError(msg, false);
      });
  };

  const cached = getCachedGoogleContactsToken();
  if (cached) {
    runWithToken(cached);
    return;
  }

  if (typeof window === "undefined" || !window.google?.accounts?.oauth2) {
    onError("Google Identity Services belum dimuat. Coba refresh halaman.");
    return;
  }

  const tokenClient = window.google.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: `${GOOGLE_CONTACTS_SCOPE} ${GOOGLE_USERINFO_SCOPES}`,
    callback: (response) => {
      if (response.error) {
        if (response.error !== "popup_closed_by_user") {
          onError(response.error);
        }
        return;
      }
      const token = response.access_token ? String(response.access_token).trim() : "";
      if (!token) {
        onError("Token dari Google tidak diterima. Silakan coba lagi.");
        return;
      }
      setCachedGoogleContactsToken(token);
      runWithToken(token);
    },
  });
  tokenClient.requestAccessToken();
}
